import { ENDPOINT } from '~/constants/endpoints';

import { type UUID } from '~/types/common';

import { type KyRequestOptions } from '@vestigas/data';

import { vestigasApi } from '~/services/kyClient';

import { type SupplierDetailResponse } from '../types';
import { zodSupplierDetailResponseSchema } from '../validations';

import { type UpdateSupplierResponsiblePersonScopePayload } from './types';
import { zodUpdateSupplierResponsiblePersonScopePayloadSchema } from './validations';

/**
 * Re-scopes one responsible person already attached to a supplier
 * relationship, leaving the others in place. 404 if the given data exchange
 * ID is not the relationship the assignment was made on.
 *
 * @param deId - The data exchange ID of the supplier relationship.
 * @param userId - The user ID of the responsible person to re-scope.
 * @param payload - The new visibility scope.
 * @param options - Optional ky request options (e.g., abort signal).
 * @returns The refreshed supplier detail response, whose owner list may now
 * differ on this supplier's other relationships in the account.
 *
 * @see https://app.dev.vestigas.com/redoc#tag/Suppliers/operation/update_supplier_responsible_person_suppliers__de_id__responsible_persons__user_id__patch
 */
export const updateSupplierResponsiblePersonScope = async (
  deId: UUID,
  userId: UUID,
  payload: UpdateSupplierResponsiblePersonScopePayload,
  options?: KyRequestOptions,
) =>
  vestigasApi
    .patch(ENDPOINT.SUPPLIER.UPDATE_RESPONSIBLE_PERSON(deId, userId), {
      json: payload,
      ...options,
      validate: {
        body: zodUpdateSupplierResponsiblePersonScopePayloadSchema,
        response: zodSupplierDetailResponseSchema,
      },
    })
    .json<SupplierDetailResponse>();
